import React, { useState, useEffect, useRef, useCallback } from 'react';
import { systemLogsAPI } from '../services/api';
import { PageLayout } from './ui';

const LEVEL_CONFIG = {
  CRITICAL: { label: 'CRT', color: 'bg-purple-600', textColor: 'text-purple-300', badgeColor: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  ERROR:    { label: 'ERR', color: 'bg-red-600', textColor: 'text-red-400', badgeColor: 'bg-red-500/20 text-red-400 border-red-500/30' },
  WARNING:  { label: 'WRN', color: 'bg-yellow-600', textColor: 'text-yellow-400', badgeColor: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  INFO:     { label: 'INF', color: 'bg-primary', textColor: 'text-blue-300', badgeColor: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  DEBUG:    { label: 'DBG', color: 'bg-gray-600', textColor: 'text-gray-400', badgeColor: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
};

const TIME_RANGES = [
  { value: '1', label: 'Last 1 hour' },
  { value: '6', label: 'Last 6 hours' },
  { value: '12', label: 'Last 12 hours' },
  { value: '24', label: 'Last 24 hours' },
  { value: '48', label: 'Last 48 hours' },
  { value: '168', label: 'Last 7 days' },
  { value: 'all', label: 'All time' },
];

const SystemLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(0);
  const [levelCounts, setLevelCounts] = useState({});
  const [loggers, setLoggers] = useState([]);

  // Filters
  const [activeLevels, setActiveLevels] = useState(new Set(['CRITICAL', 'ERROR', 'WARNING', 'INFO']));
  const [selectedLogger, setSelectedLogger] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [timeRange, setTimeRange] = useState('24');
  const [limit] = useState(500);
  const [offset, setOffset] = useState(0);

  // Live mode
  const [liveMode, setLiveMode] = useState(false);
  const liveIntervalRef = useRef(null);

  // Expanded rows
  const [expandedRows, setExpandedRows] = useState(new Set());

  const fetchLogs = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      setError('');

      const levels = Array.from(activeLevels).join(',');
      const data = await systemLogsAPI.getLogs({
        levels: levels || undefined,
        logger: selectedLogger || undefined,
        search: searchTerm || undefined,
        hours: timeRange,
        limit,
        offset,
      });

      if (data.status === 'success') {
        setLogs(data.logs || []);
        setTotal(data.total || 0);
        setLevelCounts(data.level_counts || {});
        setLoggers(data.loggers || []);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch logs');
    } finally {
      setLoading(false);
    }
  }, [activeLevels, selectedLogger, searchTerm, timeRange, limit, offset]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Live mode polling
  useEffect(() => {
    if (liveMode) {
      liveIntervalRef.current = setInterval(() => {
        fetchLogs(false);
      }, 3000);
    } else if (liveIntervalRef.current) {
      clearInterval(liveIntervalRef.current);
      liveIntervalRef.current = null;
    }
    return () => {
      if (liveIntervalRef.current) {
        clearInterval(liveIntervalRef.current);
      }
    };
  }, [liveMode, fetchLogs]);

  const toggleLevel = (level) => {
    setActiveLevels(prev => {
      const next = new Set(prev);
      if (next.has(level)) {
        next.delete(level);
      } else {
        next.add(level);
      }
      return next;
    });
    setOffset(0);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchTerm(searchInput);
    setOffset(0);
  };

  const toggleRow = (idx) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  const handleDownload = () => {
    const url = systemLogsAPI.getDownloadUrl();
    // Download with auth via HttpOnly cookies
    const link = document.createElement('a');
    link.href = url;
    fetch(url, {
      credentials: 'include'
    }).then(res => res.blob()).then(blob => {
      const blobUrl = window.URL.createObjectURL(blob);
      link.href = blobUrl;
      link.download = 'app.log';
      link.click();
      window.URL.revokeObjectURL(blobUrl);
    }).catch(() => {
      setError('Failed to download log file');
    });
  };

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  // Summary counts string
  const summaryParts = [];
  Object.entries(levelCounts).forEach(([level, count]) => {
    if (count > 0) {
      summaryParts.push({ level, count });
    }
  });
  summaryParts.sort((a, b) => {
    const order = { CRITICAL: 0, ERROR: 1, WARNING: 2, INFO: 3, DEBUG: 4 };
    return (order[a.level] ?? 5) - (order[b.level] ?? 5);
  });

  return (
    <PageLayout 
      title="System Logs" 
      subtitle="Real-time application logs and debugging information"
      backTo="/management"
    >
      <div className="bg-gray-950 text-gray-200 rounded-lg border border-gray-800 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <svg className="w-6 h-6 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h2 className="text-xl font-bold text-white">Terminal View</h2>
          </div>
          <div className="flex items-center gap-4 text-sm">
            {/* Summary counts */}
            <div className="flex items-center gap-2">
              {summaryParts.map(({ level, count }) => {
                const config = LEVEL_CONFIG[level];
                return (
                  <span key={level} className={`${config?.textColor || 'text-gray-400'}`}>
                    {count} {level.toLowerCase()}
                  </span>
                );
              })}
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 mb-4 bg-gray-900 rounded-lg p-3 border border-gray-800">
          {/* Live toggle */}
          <button
            onClick={() => setLiveMode(!liveMode)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              liveMode
                ? 'bg-green-600 text-white shadow-lg shadow-green-600/20'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            <span className={`w-2 h-2 rounded-full ${liveMode ? 'bg-white animate-pulse' : 'bg-gray-500'}`} />
            Live
          </button>

          {/* Level filters */}
          <div className="flex items-center gap-1">
            {Object.entries(LEVEL_CONFIG).map(([level, config]) => (
              <button
                key={level}
                onClick={() => toggleLevel(level)}
                className={`px-2.5 py-1.5 rounded text-xs font-bold transition-all border ${
                  activeLevels.has(level)
                    ? `${config.badgeColor} border`
                    : 'bg-gray-800 text-gray-500 border-gray-700 hover:bg-gray-700'
                }`}
              >
                {config.label}
              </button>
            ))}
          </div>

          {/* Separator */}
          <div className="w-px h-6 bg-gray-700" />

          {/* Time range */}
          <select
            value={timeRange}
            onChange={(e) => { setTimeRange(e.target.value); setOffset(0); }}
            className="bg-gray-800 border border-gray-700 text-gray-300 text-sm rounded px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary"
          >
            {TIME_RANGES.map(tr => (
              <option key={tr.value} value={tr.value}>{tr.label}</option>
            ))}
          </select>

          {/* Logger filter */}
          <select
            value={selectedLogger}
            onChange={(e) => { setSelectedLogger(e.target.value); setOffset(0); }}
            className="bg-gray-800 border border-gray-700 text-gray-300 text-sm rounded px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option value="">All loggers</option>
            {loggers.map(l => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>

          {/* Search */}
          <form onSubmit={handleSearch} className="flex items-center gap-1">
            <input
              type="text"
              placeholder="Search logs..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="bg-gray-800 border border-gray-700 text-gray-300 text-sm rounded px-3 py-1.5 w-48 focus:outline-none focus:ring-1 focus:ring-primary placeholder-gray-500"
            />
            <button
              type="submit"
              className="bg-gray-700 hover:bg-gray-600 text-gray-300 px-2.5 py-1.5 rounded text-sm transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>
          </form>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Actions */}
          <button
            onClick={() => fetchLogs()}
            className="bg-gray-700 hover:bg-gray-600 text-gray-300 px-3 py-1.5 rounded text-sm transition-colors"
            title="Refresh"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          <button
            onClick={handleDownload}
            className="bg-gray-700 hover:bg-gray-600 text-gray-300 px-3 py-1.5 rounded text-sm transition-colors"
            title="Download log file"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-900/30 border border-red-700 text-red-300 p-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-400"></div>
            <span className="ml-3 text-gray-400">Loading logs...</span>
          </div>
        )}

        {/* Log table */}
        {!loading && (
          <>
            <div className="bg-gray-900 rounded-lg border border-gray-800 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-800 text-gray-500 text-xs uppercase">
                      <th className="text-left px-4 py-2.5 w-44">Timestamp</th>
                      <th className="text-left px-3 py-2.5 w-20">Level</th>
                      <th className="text-left px-3 py-2.5 w-48">Logger</th>
                      <th className="text-left px-3 py-2.5 w-36">Route</th>
                      <th className="text-left px-3 py-2.5">Message</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono text-xs">
                    {logs.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center py-12 text-gray-500">
                          No log entries found matching your filters.
                        </td>
                      </tr>
                    ) : (
                      logs.map((log, idx) => {
                        const config = LEVEL_CONFIG[log.level] || LEVEL_CONFIG.DEBUG;
                        const isExpanded = expandedRows.has(idx);
                        const isMultiLine = log.message && log.message.includes('\n');

                        return (
                          <tr
                            key={idx}
                            onClick={() => isMultiLine && toggleRow(idx)}
                            className={`border-b border-gray-800/50 hover:bg-gray-800/50 transition-colors ${
                              isMultiLine ? 'cursor-pointer' : ''
                            } ${log.level === 'ERROR' || log.level === 'CRITICAL' ? 'bg-red-950/20' : ''}`}
                          >
                            <td className="px-4 py-2 text-gray-500 whitespace-nowrap">
                              {log.timestamp ? formatTimestamp(log.timestamp) : '-'}
                            </td>
                            <td className="px-3 py-2">
                              <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-bold ${config.badgeColor} border`}>
                                {config.label}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-indigo-300 truncate max-w-[12rem]" title={log.logger}>
                              {log.logger}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {log.method && (
                                <span className="text-green-400 mr-1">{log.method}</span>
                              )}
                              {log.route && (
                                <span className="text-gray-400">{log.route}</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-gray-300">
                              <div className={`${isExpanded ? '' : 'truncate max-w-[60rem]'}`}>
                                {isExpanded ? (
                                  <pre className="whitespace-pre-wrap break-all text-xs">{log.message}</pre>
                                ) : (
                                  log.message.split('\n')[0]
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {total > limit && (
              <div className="flex items-center justify-between mt-4 text-sm text-gray-400">
                <span>
                  Showing {offset + 1}-{Math.min(offset + limit, total)} of {total} entries
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setOffset(Math.max(0, offset - limit))}
                    disabled={offset === 0}
                    className="px-3 py-1.5 rounded bg-gray-800 border border-gray-700 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <span className="px-3">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setOffset(offset + limit)}
                    disabled={offset + limit >= total}
                    className="px-3 py-1.5 rounded bg-gray-800 border border-gray-700 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {/* Footer info */}
            <div className="mt-3 text-xs text-gray-600 flex items-center justify-between">
              <span>{total} total entries matching filters</span>
              {liveMode && (
                <span className="flex items-center gap-1.5 text-green-500">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                  Auto-refreshing every 3s
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </PageLayout>
  );
};

function formatTimestamp(ts) {
  try {
    const date = new Date(ts);
    if (isNaN(date.getTime())) return ts;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const h = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    const s = String(date.getSeconds()).padStart(2, '0');
    const ms = String(date.getMilliseconds()).padStart(3, '0');
    return `${y}-${m}-${d} ${h}:${min}:${s}.${ms}`;
  } catch {
    return ts;
  }
}

export default SystemLogsPage;
